import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiURl from "../Paths/Api";

const StarBorder = ({
  as: Component = 'button',
  className = '',
  color = '#22C55E',
  speed = '3s',
  thickness = 1.5,
  children,
  ...rest
}) => {
  return (
    <Component
      className={`relative inline-block overflow-hidden rounded-full ${className} hover:scale-105 transition-transform duration-200`}
      style={{ padding: `${thickness}px 0`, ...rest.style }}
      {...rest}
    >
      <div
        className="absolute w-[300%] h-[50%] opacity-50 bottom-[-10px] right-[-250%] rounded-full animate-star-movement z-0"
        style={{ background: `radial-gradient(circle, ${color}, transparent 20%)`, animationDuration: speed }}
      ></div>
      <div
        className="absolute w-[300%] h-[50%] opacity-50 top-[-10px] left-[-250%] rounded-full animate-star-movement z-0"
        style={{ background: `radial-gradient(circle, ${color}, transparent 20%)`, animationDuration: speed }}
      ></div>
      <div className="relative z-1 bg-gradient-to-r from-green-600 to-emerald-500 border border-green-700 text-white text-sm font-medium py-2 px-4 rounded-full shadow-sm hover:shadow-md transition-shadow duration-200">
        {children}
      </div>
    </Component>
  );
};

const PublicEvents = () => {
  const [meetings, setMeetings] = useState([]);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const [numTickets, setNumTickets] = useState('1');
  const [success, setSuccess] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRazorpayLoaded, setIsRazorpayLoaded] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [customFormFields, setCustomFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [visibleConditionalFields, setVisibleConditionalFields] = useState({});
  const [maxTicketsPerBooking, setMaxTicketsPerBooking] = useState(1);
  const [showEventSelection, setShowEventSelection] = useState(true);
  const navigate = useNavigate();

  const RAZORPAY_KEY_ID = 'rzp_live_RKcrO8IhwM2Xj6';

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => {
      setIsRazorpayLoaded(true);
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Razorpay Script Loaded`);
    };
    script.onerror = () => {
      setWarnings(['Payment gateway failed to load.']);
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Razorpay Script Error`);
    };
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    fetchPublicMeetings();
  }, []);

  const fetchPublicMeetings = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${ApiURl}/public-meetings.php`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      const data = await response.json();
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Meetings API Response`, data);
      if (data.success && Array.isArray(data.meetings)) {
        const formattedMeetings = data.meetings.filter((meeting) => meeting.is_public == 1);
        setMeetings(formattedMeetings);

        // If only one event exists, auto-select it and show form directly
        if (formattedMeetings.length === 1) {
          const firstMeeting = formattedMeetings[0];
          handleMeetingSelect(firstMeeting.id, true, firstMeeting);
          setShowEventSelection(false);
        } else if (formattedMeetings.length > 0) {
          setShowEventSelection(true);
        }
      } else {
        setWarnings([data.message || 'No events available.']);
        console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Meetings Error`, { error: data.message || 'Invalid response' });
      }
    } catch (err) {
      setWarnings(['Failed to fetch events.']);
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Meetings API Error`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleHomeClick = () => {
    navigate('/');
    console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Navigated to Home`);
  };

  const handleEventCardClick = (meetingId, meetingObj) => {
    handleMeetingSelect(meetingId, false, meetingObj);
    setShowEventSelection(false);
  };

  const handleBackToEvents = () => {
    setShowEventSelection(true);
    setSelectedMeetingId(null);
    setSuccess('');
    setWarnings([]);
    setFormData({});
    setVisibleConditionalFields({});
    setNumTickets('1');
    setIsFormSubmitting(false);
  };

  const handleMeetingSelect = (meetingId, initialLoad = false, meetingObj = null) => {
    const selectedId = meetingId.toString();
    setSelectedMeetingId(selectedId);
    setSuccess('');
    setWarnings([]);
    setIsFormSubmitting(false);

    const selectedMeeting = meetingObj || meetings.find(m => m.id.toString() === selectedId);

    if (!selectedMeeting) return;

    if (selectedMeeting?.custom_form && Array.isArray(selectedMeeting.custom_form)) {
      setCustomFormFields(selectedMeeting.custom_form);

      const initialFormData = {};
      const initialConditionalFields = {};

      selectedMeeting.custom_form.forEach(field => {
        const fieldId = field.fieldName || field.id;
        initialFormData[fieldId] = '';
        if (field.conditionalFields) initialConditionalFields[fieldId] = false;
      });

      setFormData(initialFormData);
      setVisibleConditionalFields(initialConditionalFields);
    } else {
      setCustomFormFields([]);
      setFormData({});
      setVisibleConditionalFields({});
    }

    const maxTickets = selectedMeeting?.max_tickets_per_booking || 1;
    setMaxTicketsPerBooking(maxTickets);
    setNumTickets('1');
  };

  const handleFormDataChange = (fieldName, value) => {
    const selectedMeeting = meetings.find((m) => m.id.toString() === selectedMeetingId);
    const field = selectedMeeting?.custom_form?.find(f => (f.fieldName || f.id) === fieldName);

    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // FIX: Update for both 'select' and 'radio' types
    if (field?.conditionalFields && (field.type === 'select' || field.type === 'radio')) {
      setVisibleConditionalFields(prev => ({
        ...prev,
        [fieldName]: value
      }));
    }
  };

  const validateInputs = () => {
    const selectedMeeting = meetings.find((m) => m.id.toString() === selectedMeetingId);

    if (!selectedMeeting?.custom_form || !Array.isArray(selectedMeeting.custom_form)) {
      return 'This event does not have a registration form.';
    }

    if (selectedMeeting.custom_form.length === 0) {
      return 'No registration fields configured for this event.';
    }

    const errors = [];

    selectedMeeting.custom_form.forEach(field => {
      const fieldId = field.fieldName || field.id;
      const value = formData[fieldId] || '';
      const fieldLabel = field.label || field.fieldName || 'Field';

      if (field.required && !value.trim()) {
        errors.push(`${fieldLabel} is required.`);
      }

      if (field.type === 'email' && value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
        errors.push(`Invalid email address in ${fieldLabel}.`);
      }

      if (field.type === 'tel' && value.trim()) {
        const phoneValue = value.trim().replace(/^\+?91/, '');
        if (!/^\d{10}$/.test(phoneValue)) {
          errors.push(`Invalid 10-digit phone number in ${fieldLabel}.`);
        }
      }

      // FIX: Check conditional fields for both radio and select
      if (field.conditionalFields && value && (field.type === 'select' || field.type === 'radio')) {
        const conditionalFields = field.conditionalFields[value];

        if (conditionalFields && Array.isArray(conditionalFields)) {
          conditionalFields.forEach(condField => {
            const condFieldId = condField.fieldName || condField.id;
            const condValue = formData[condFieldId] || '';
            const condLabel = condField.label || condField.fieldName || 'Field';

            if (condField.required && !condValue.trim()) {
              errors.push(`${condLabel} is required for ${value}.`);
            }
          });
        }
      }
    });

    if (errors.length > 0) {
      return errors[0];
    }

    return '';
  };

  // NEW: Simple form submission function
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (isLoading || isFormSubmitting) return;

    if (!selectedMeetingId) {
      setWarnings(['Select an event.']);
      return;
    }

    const selectedMeeting = meetings.find((m) => m.id.toString() === selectedMeetingId);
    if (!selectedMeeting) {
      setWarnings(['Event unavailable.']);
      return;
    }

    if (!selectedMeeting?.custom_form || !Array.isArray(selectedMeeting.custom_form) || selectedMeeting.custom_form.length === 0) {
      setWarnings(['This event does not have a registration form.']);
      return;
    }

    const validationError = validateInputs();
    if (validationError) {
      setWarnings([validationError]);
      return;
    }

    const currentAttendeeCount = selectedMeeting.attendee_count ?? (selectedMeeting.join_requests?.length || 0);
    const availableSpots = selectedMeeting.capacity - currentAttendeeCount;
    const ticketCount = parseInt(numTickets) || 1;

    const convertFormDataToLabels = (customFormFields, formData) => {
      const converted = {};

      customFormFields.forEach(field => {
        const fieldId = field.fieldName || field.id;
        const keyName = field.label || field.fieldName || fieldId;

        // main field
        if (formData[fieldId] !== undefined && formData[fieldId] !== "") {
          converted[keyName] = formData[fieldId];
        }

        // conditional fields
        if (field.conditionalFields && formData[fieldId]) {
          const selectedValue = formData[fieldId];
          const conditionalSet = field.conditionalFields[selectedValue];

          if (conditionalSet && Array.isArray(conditionalSet)) {
            conditionalSet.forEach(condField => {
              const condFieldId = condField.fieldName || condField.id;
              const condKeyName = condField.label || condField.fieldName || condFieldId;

              if (formData[condFieldId] !== undefined && formData[condFieldId] !== "") {
                converted[condKeyName] = formData[condFieldId];
              }
            });
          }
        }
      });

      return converted;
    };

    if (availableSpots < ticketCount) {
      setWarnings([`Only ${availableSpots} ticket(s) available.`]);
      return;
    }

    setIsFormSubmitting(true);

    try {
      const readableFormData = convertFormDataToLabels(customFormFields, formData);

      const submitData = {
        meetingId: selectedMeetingId,
        meetingTitle: selectedMeeting.title,
        numTickets: ticketCount,
        totalAmount: selectedMeeting.price * ticketCount,
        customFormData: readableFormData,
        submittedAt: new Date().toISOString()
      };

      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Submitting form data`, submitData);

      // NEW: Submit to simple storage PHP file
      const response = await fetch(`${ApiURl}/store-event-registration.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(submitData),
      });

      const responseText = await response.text();
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Submit Response:`, responseText);

      if (!responseText || responseText.trim() === '') {
        setWarnings(['Server returned empty response.']);
        setIsFormSubmitting(false);
        return;
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (jsonError) {
        console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] JSON Parse Error:`, jsonError);
        setWarnings(['Server returned invalid response.']);
        setIsFormSubmitting(false);
        return;
      }

      if (data.success) {
        setSuccess('Your registration has been submitted successfully!');
        setWarnings([]);

        // Clear form after successful submission
        setTimeout(() => {
          const initialFormData = {};
          const initialConditionalFields = {};
          selectedMeeting.custom_form.forEach(field => {
            const fieldId = field.fieldName || field.id;
            initialFormData[fieldId] = '';
            if (field.conditionalFields) {
              initialConditionalFields[fieldId] = false;
            }
          });
          setFormData(initialFormData);
          setVisibleConditionalFields(initialConditionalFields);
          setNumTickets('1');
        }, 100);
      } else {
        setWarnings([data.message || 'Submission failed. Please try again.']);
      }
    } catch (err) {
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Submit Error`, err);
      setWarnings(['Network error. Please check your connection and try again.']);
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const renderCustomFormField = (field) => {
    const fieldId = field.fieldName || field.id;
    const value = formData[fieldId] || '';
    const label = field.label || field.fieldName || 'Field';
    const placeholder = field.placeholder || `Enter ${label.toLowerCase()}`;
    const isRequired = field.required;

    switch (field.type) {
      case 'email':
        return (
          <div key={fieldId} className="col-span-1 md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {label} {isRequired && <span className="text-red-500">*</span>}
            </label>
            <input
              type="email"
              value={value}
              onChange={(e) => handleFormDataChange(fieldId, e.target.value)}
              placeholder={placeholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-green-500 focus:border-green-500 bg-white text-gray-800"
              required={isRequired}
              disabled={isLoading || isPaymentProcessing || isFormSubmitting}
            />
          </div>
        );

      case 'tel':
        return (
          <div key={fieldId} className="col-span-1 md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {label} {isRequired && <span className="text-red-500">*</span>}
            </label>
            <input
              type="tel"
              value={value}
              onChange={(e) => handleFormDataChange(fieldId, e.target.value)}
              placeholder={placeholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-green-500 focus:border-green-500 bg-white text-gray-800"
              required={isRequired}
              disabled={isLoading || isPaymentProcessing || isFormSubmitting}
            />
          </div>
        );

      case 'number':
        return (
          <div key={fieldId} className="col-span-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {label} {isRequired && <span className="text-red-500">*</span>}
            </label>
            <input
              type="number"
              value={value}
              onChange={(e) => handleFormDataChange(fieldId, e.target.value)}
              placeholder={placeholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-green-500 focus:border-green-500 bg-white text-gray-800 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              required={isRequired}
              disabled={isLoading || isPaymentProcessing || isFormSubmitting}
            />
          </div>
        );

      case 'textarea':
        return (
          <div key={fieldId} className="col-span-1 md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {label} {isRequired && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={value}
              onChange={(e) => handleFormDataChange(fieldId, e.target.value)}
              placeholder={placeholder}
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-green-500 focus:border-green-500 bg-white text-gray-800 resize-none"
              required={isRequired}
              disabled={isLoading || isPaymentProcessing || isFormSubmitting}
            />
          </div>
        );

      case 'select':
        return (
          <div key={fieldId} className="col-span-1 md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {label} {isRequired && <span className="text-red-500">*</span>}
            </label>
            <select
              value={value}
              onChange={(e) => handleFormDataChange(fieldId, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-green-500 focus:border-green-500 bg-white text-gray-800"
              required={isRequired}
              disabled={isLoading || isPaymentProcessing || isFormSubmitting}
            >
              <option value="">Select {label}</option>
              {field.options && field.options.map((option, index) => (
                <option key={index} value={option}>{option}</option>
              ))}
            </select>

            {field.conditionalFields && value && field.conditionalFields[value] && (
              <div className="mt-3 p-3 bg-green-50 rounded border border-green-200 animate-fade-in">
                <p className="text-xs font-medium text-green-800 mb-2">Additional information required for {value} ticket:</p>
                <div className="space-y-2">
                  {field.conditionalFields[value].map((condField, index) => {
                    const condFieldId = condField.fieldName || condField.id;
                    const condValue = formData[condFieldId] || '';
                    const condLabel = condField.label || condField.fieldName || 'Field';
                    const condPlaceholder = condField.placeholder || `Enter ${condLabel.toLowerCase()}`;
                    const condRequired = condField.required;

                    return (
                      <div key={condFieldId}>
                        <label className="block text-xs font-medium text-green-800 mb-1">
                          {condLabel} {condRequired && <span className="text-red-500">*</span>}
                        </label>
                        {condField.type === 'textarea' ? (
                          <textarea
                            value={condValue}
                            onChange={(e) => handleFormDataChange(condFieldId, e.target.value)}
                            placeholder={condPlaceholder}
                            rows="1"
                            className="w-full px-3 py-2 border border-green-300 rounded-md text-sm focus:ring-1 focus:ring-green-500 focus:border-green-500 bg-white text-gray-800 resize-none"
                            required={condRequired}
                            disabled={isLoading || isPaymentProcessing || isFormSubmitting}
                          />
                        ) : condField.type === 'radio' ? (
                          <div className="space-y-1">
                            {condField.options && condField.options.map((option, optIndex) => (
                              <label key={optIndex} className="flex items-center text-xs text-green-800 cursor-pointer">
                                <input
                                  type="radio"
                                  name={condFieldId}
                                  value={option}
                                  checked={condValue === option}
                                  onChange={(e) => handleFormDataChange(condFieldId, e.target.value)}
                                  className="h-3 w-3 text-green-600 focus:ring-green-500 border-green-300"
                                  required={condRequired}
                                  disabled={isLoading || isPaymentProcessing || isFormSubmitting}
                                />
                                <span className="ml-2">{option}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <input
                            type={condField.type === 'email' ? 'email' : condField.type === 'number' ? 'number' : 'text'}
                            value={condValue}
                            onChange={(e) => handleFormDataChange(condFieldId, e.target.value)}
                            placeholder={condPlaceholder}
                            className="w-full px-3 py-2 border border-green-300 rounded-md text-sm focus:ring-1 focus:ring-green-500 focus:border-green-500 bg-white text-gray-800"
                            required={condRequired}
                            disabled={isLoading || isPaymentProcessing || isFormSubmitting}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );

      case 'radio':
        return (
          <div key={fieldId} className="col-span-1 md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-2">
              {label} {isRequired && <span className="text-red-500">*</span>}
            </label>
            <div className="space-y-2">
              {field.options && field.options.map((option, index) => (
                <label key={index} className="flex items-center text-sm text-gray-800 cursor-pointer hover:bg-gray-50 p-2 rounded">
                  <input
                    type="radio"
                    name={fieldId}
                    value={option}
                    checked={value === option}
                    onChange={(e) => handleFormDataChange(fieldId, e.target.value)}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                    required={isRequired}
                    disabled={isLoading || isPaymentProcessing || isFormSubmitting}
                  />
                  <span className="ml-3">{option}</span>
                </label>
              ))}
            </div>

            {/* SHOW CONDITIONAL FIELDS FOR RADIO BUTTONS */}
            {field.conditionalFields && value && field.conditionalFields[value] && (
              <div className="mt-3 p-3 bg-green-50 rounded border border-green-200 animate-fade-in">
                <p className="text-xs font-medium text-green-800 mb-2">Additional information for {value}:</p>
                <div className="space-y-2">
                  {field.conditionalFields[value].map((condField, index) => {
                    const condFieldId = condField.fieldName || condField.id;
                    const condValue = formData[condFieldId] || '';
                    const condLabel = condField.label || condField.fieldName || 'Field';
                    const condPlaceholder = condField.placeholder || `Enter ${condLabel.toLowerCase()}`;
                    const condRequired = condField.required;

                    return (
                      <div key={condFieldId}>
                        <label className="block text-xs font-medium text-green-800 mb-1">
                          {condLabel} {condRequired && <span className="text-red-500">*</span>}
                        </label>
                        {condField.type === 'textarea' ? (
                          <textarea
                            value={condValue}
                            onChange={(e) => handleFormDataChange(condFieldId, e.target.value)}
                            placeholder={condPlaceholder}
                            rows="1"
                            className="w-full px-3 py-2 border border-green-300 rounded-md text-sm focus:ring-1 focus:ring-green-500 focus:border-green-500 bg-white text-gray-800 resize-none"
                            required={condRequired}
                            disabled={isLoading || isPaymentProcessing || isFormSubmitting}
                          />
                        ) : (
                          <input
                            type={condField.type === 'email' ? 'email' : condField.type === 'number' ? 'number' : 'text'}
                            value={condValue}
                            onChange={(e) => handleFormDataChange(condFieldId, e.target.value)}
                            placeholder={condPlaceholder}
                            className="w-full px-3 py-2 border border-green-300 rounded-md text-sm focus:ring-1 focus:ring-green-500 focus:border-green-500 bg-white text-gray-800"
                            required={condRequired}
                            disabled={isLoading || isPaymentProcessing || isFormSubmitting}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div key={fieldId} className="col-span-1 md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {label} {isRequired && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => handleFormDataChange(fieldId, e.target.value)}
              placeholder={placeholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-green-500 focus:border-green-500 bg-white text-gray-800"
              required={isRequired}
              disabled={isLoading || isPaymentProcessing || isFormSubmitting}
            />
          </div>
        );
    }
  };

  const selectedMeeting =
    selectedMeetingId && meetings.length > 0
      ? meetings.find((m) => m.id.toString() === selectedMeetingId)
      : null;
  const currentAttendeeCount = selectedMeeting ? (selectedMeeting.attendee_count ?? (selectedMeeting.join_requests?.length || 0)) : 0;
  const availableSpots = selectedMeeting ? selectedMeeting.capacity - currentAttendeeCount : 0;
  const isFreeEvent = selectedMeeting && Number(selectedMeeting.price) === 0;

  return (
    <div className="min-h-screen font-sans bg-gray-50">
      {/* Loading Overlay */}
      {(isLoading || isPaymentProcessing || isFormSubmitting) && (
        <div className="fixed inset-0 bg-white/90 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-2xl shadow-2xl flex flex-col items-center border border-green-100 max-w-sm">
            <div className="animate-spin rounded-full h-14 w-14 border-t-3 border-b-3 border-green-600 mb-4"></div>
            <p className="text-green-700 text-lg font-medium mb-2">
              {isFormSubmitting ? 'Submitting Registration...' : 
               isPaymentProcessing ? 'Confirming Tickets...' : 'Loading Events...'}
            </p>
            <p className="text-green-600 text-sm text-center">
              {isFormSubmitting ? 'Please wait while we process your registration' : 
               'Do not close the window until complete'}
            </p>
          </div>
        </div>
      )}

      {/* Success Popup */}
      {success && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Background Blur Overlay */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSuccess('')}></div>
          
          {/* Success Popup */}
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full animate-fade-in">
            <div className="p-6 text-center">
              {/* Success Icon */}
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4 mx-auto">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              {/* Success Title */}
              <h3 className="text-xl font-bold text-gray-900 mb-2">Registration Submitted!</h3>
              
              {/* Success Message */}
              <p className="text-gray-600 mb-4">
                Your registration has been submitted successfully. We have received your details and will contact you soon.
              </p>
              
              {/* Additional Info */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-center text-xs text-green-700">
                  <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Due to limited capacity, entry will be on a first come first serve basis.</span>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => setSuccess('')}
                  className="flex-1 px-4 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                >
                  Done
                </button>
                {meetings.length > 1 && (
                  <button
                    onClick={() => {
                      setSuccess('');
                      handleBackToEvents();
                    }}
                    className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    View Other Events
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 bg-green-100 rounded-lg">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-base sm:text-lg font-bold text-gray-900">Event Manager</h1>
          </div>
          <button
            onClick={handleHomeClick}
            className="px-3 sm:px-4 py-1.5 bg-green-600 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-green-700 transition-colors shadow-sm"
            disabled={isLoading || isPaymentProcessing || isFormSubmitting}
          >
            Home
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4">
        {/* Show Event Selection Grid for Multiple Events */}
        {showEventSelection && meetings.length > 1 ? (
          <div className="animate-slide-in">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Upcoming Events</h1>
                <p className="text-gray-600 text-xs mt-1">Select an event to register</p>
              </div>
              <div className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-lg">
                {meetings.length} events available
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {meetings.map((meeting) => {
                const attendeeCount = meeting.attendee_count || 0;
                const availableSpots = meeting.capacity - attendeeCount;
                const isFree = meeting.price == 0 || meeting.price == '0';

                return (
                  <div
                    key={meeting.id}
                    className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow transition-all duration-200 hover:-translate-y-0.5 cursor-pointer group animate-fade-up"
                    onClick={() => !isLoading && !isPaymentProcessing && !isFormSubmitting && handleEventCardClick(meeting.id, meeting)}
                  >
                    {/* Event Image - FULLY VISIBLE with proper aspect ratio */}
                    <div className="relative w-full h-40 bg-gray-100 overflow-hidden">
                      {meeting.image_url ? (
                        <img
                          src={`${ApiURl}/${meeting.image_url}`}
                          alt={meeting.title}
                          className="w-full h-full object-contain bg-white"
                          style={{ objectFit: 'contain' }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center">
                          <svg className="w-8 h-8 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                      <div className="absolute top-2 right-2">
                        <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${isFree ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'}`}>
                          {isFree ? 'Free' : `₹${meeting.price}`}
                        </span>
                      </div>
                      {meeting.restricted == 1 && (
                        <div className="absolute top-2 left-2">
                          <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold bg-red-500 text-white">
                            Counter Only
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="p-3">
                      <div className="flex items-start justify-between mb-1">
                        <h3 className="font-bold text-gray-900 text-sm line-clamp-1">{meeting.title}</h3>
                      </div>

                      <div className="flex items-center text-xs text-gray-500 mb-1">
                        <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        </svg>
                        <span className="truncate">{meeting.location || 'Online'}</span>
                      </div>

                      <div className="flex items-center text-xs text-gray-500 mb-2">
                        <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>
                          {meeting.time && !isNaN(new Date(meeting.time).getTime())
                            ? new Date(meeting.time).toLocaleString('en-US', {
                              timeZone: 'Asia/Kolkata',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                            : 'TBA'}
                        </span>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        <div className={`text-xs font-medium ${availableSpots > 5 ? 'text-green-600' : availableSpots > 0 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {availableSpots > 0 ? `${availableSpots} seats` : 'Sold Out'}
                        </div>
                        <button className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 transition-colors group-hover:bg-green-700">
                          Register
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {meetings.length === 0 && !isLoading && (
              <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h3 className="text-lg font-bold text-gray-900 mb-1">No Events Available</h3>
                <p className="text-gray-600 text-sm">There are currently no public events to register.</p>
              </div>
            )}
          </div>
        ) : (
          // Show Registration Form (for single event or after selection)
          <div className={`animate-slide-in ${!showEventSelection ? 'animate-slide-in' : ''}`}>
            {selectedMeeting ? (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                {/* Event Header - FULLY VISIBLE Image with NO Overlay */}
                <div className="relative w-full bg-gray-100">
                  {selectedMeeting.image_url ? (
                    <div className="w-full h-56 sm:h-64 flex items-center justify-center bg-white">
                      <img
                        src={`${ApiURl}/${selectedMeeting.image_url}`}
                        alt={selectedMeeting.title}
                        className="w-full h-full object-contain"
                        style={{ 
                          objectFit: 'contain',
                          maxWidth: '100%',
                          maxHeight: '100%'
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-32 sm:h-40 bg-gradient-to-r from-green-600 to-emerald-600"></div>
                  )}

                  {/* Back Button for Multiple Events - Placed on top right */}
                  {meetings.length > 1 && (
                    <button
                      onClick={handleBackToEvents}
                      className="absolute top-3 right-3 px-3 py-1 bg-white/90 backdrop-blur-sm text-gray-700 rounded text-xs font-medium hover:bg-white transition-colors shadow-sm flex items-center"
                      disabled={isLoading || isPaymentProcessing || isFormSubmitting}
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                      Back
                    </button>
                  )}
                </div>

                {/* Event Info BELOW the image - Compact Layout */}
                <div className="p-4">
                  <div className="mb-4">
                    <h2 className="text-xl font-bold text-gray-900 mb-2">{selectedMeeting.title}</h2>
                    
                    {/* Compact Info Grid */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="flex items-center text-xs text-gray-600">
                        <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        </svg>
                        <span className="truncate">{selectedMeeting.location || 'Online'}</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-600">
                        <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>
                          {selectedMeeting.time && !isNaN(new Date(selectedMeeting.time).getTime())
                            ? new Date(selectedMeeting.time).toLocaleString('en-US', {
                              timeZone: 'Asia/Kolkata',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                            : 'TBA'}
                        </span>
                      </div>
                      <div className="flex items-center text-xs">
                        <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className={`font-medium ${isFreeEvent ? 'text-blue-600' : 'text-green-600'}`}>
                          {isFreeEvent ? 'Free' : `₹${selectedMeeting.price}`}
                        </span>
                      </div>
                      <div className="flex items-center text-xs">
                        <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className={`font-medium ${availableSpots > 5 ? 'text-green-600' : availableSpots > 0 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {availableSpots} left
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Capacity Notice - ADDED */}
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start">
                      <svg className="w-4 h-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-xs font-medium text-blue-800 mb-0.5">Important Note</p>
                        <p className="text-xs text-blue-700">
                          Due to limited capacity, entry will be on a first come first serve basis. 
                          Registration does not guarantee entry.
                        </p>
                      </div>
                    </div>
                  </div>

                  {warnings.length > 0 && (
                    <div className="mb-3 p-3 bg-yellow-50 text-yellow-700 rounded text-sm animate-fade-in border border-yellow-200">
                      <div className="flex items-center mb-1">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span className="font-medium">Please check:</span>
                      </div>
                      <ul className="list-disc pl-5 text-sm space-y-1">
                        {warnings.map((warning, index) => (
                          <li key={index}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedMeeting.restricted == 1 ? (
                    <div className="text-center py-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4">
                        <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Counter Booking Required</h3>
                      <p className="text-gray-600 text-sm mb-4">This event requires booking through the physical ticket counter.</p>
                      <div className="inline-flex items-center space-x-2 text-gray-700 bg-gray-100 px-4 py-2 rounded-lg text-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 11111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="font-medium">Visit ticket counter</span>
                      </div>
                    </div>
                  ) : selectedMeeting.custom_form && selectedMeeting.custom_form.length > 0 ? (
                    <div>
                      <div className="mb-4">
                        <h3 className="text-base font-bold text-gray-900 mb-1">Registration Form</h3>
                        <p className="text-gray-600 text-xs">Fill in the details to complete your booking</p>
                      </div>

                      {/* NEW: Simple form submission */}
                      <form onSubmit={handleSubmitForm}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                          {customFormFields.map((field) => renderCustomFormField(field))}
                        </div>

                        {/* Tickets Selection - More Compact */}
                        <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Number of Tickets <span className="text-red-500">*</span>
                          </label>
                          {maxTicketsPerBooking === 1 ? (
                            <div className="flex items-center justify-between p-2 bg-white rounded border border-gray-300">
                              <span className="text-xs text-gray-700">1 ticket (max per booking)</span>
                              <span className={`text-xs font-medium ${isFreeEvent ? 'text-blue-600' : 'text-green-600'}`}>
                                {isFreeEvent ? 'Free' : `₹${selectedMeeting.price}`}
                              </span>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <div className="flex-1">
                                  <input
                                    type="number"
                                    min="1"
                                    max={Math.min(availableSpots, maxTicketsPerBooking)}
                                    value={numTickets}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setNumTickets(value === '' ? '' : Math.min(availableSpots, maxTicketsPerBooking, parseInt(value) || 1).toString());
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-green-500 focus:border-green-500 bg-white text-gray-800 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    required
                                    disabled={isLoading || isPaymentProcessing || isFormSubmitting || availableSpots < 1}
                                  />
                                </div>
                                <div className="text-xs text-gray-600 whitespace-nowrap">
                                  Max {maxTicketsPerBooking}
                                </div>
                              </div>
                              <div className="text-xs text-gray-500">
                                {availableSpots} seats available
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Summary and Submit Button - More Compact */}
                        <div className="border-t border-gray-200 pt-4">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div>
                              <div className="text-xs text-gray-500 mb-0.5">Total Amount</div>
                              <div className={`text-lg font-bold ${isFreeEvent ? 'text-blue-600' : 'text-gray-900'}`}>
                                {isFreeEvent ? 'Free' : `₹${selectedMeeting.price * (parseInt(numTickets) || 1)}`}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {numTickets || 1} ticket{(parseInt(numTickets) || 1) > 1 ? 's' : ''}
                                {!isFreeEvent && ` × ₹${selectedMeeting.price}`}
                              </div>
                            </div>

                            {/* NEW: Simple submit button */}
                            <button
                              type="submit"
                              className="px-6 py-3 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[180px]"
                              disabled={isLoading || isFormSubmitting || availableSpots < 1}
                            >
                              {isFormSubmitting ? (
                                <span className="flex items-center justify-center">
                                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Submitting...
                                </span>
                              ) : availableSpots < 1 ? (
                                'Sold Out'
                              ) : (
                                `Submit Registration`
                              )}
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-full mb-4">
                        <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Registration Not Available</h3>
                      <p className="text-gray-600 text-sm mb-4">This event does not have a registration form configured.</p>
                      <p className="text-gray-500 text-xs">Select another event or contact the organizer.</p>
                    </div>
                  )}
                </div>

                {/* Event Info Footer - More Compact */}
                <div className="border-t border-gray-200 bg-gray-50 p-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                      <div className="text-gray-500 mb-0.5">Date & Time</div>
                      <div className="font-medium text-gray-900 text-xs">
                        {selectedMeeting.time && !isNaN(new Date(selectedMeeting.time).getTime())
                          ? new Date(selectedMeeting.time).toLocaleString('en-US', {
                            timeZone: 'Asia/Kolkata',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                          : 'TBA'}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500 mb-0.5">Organizer</div>
                      <div className="font-medium text-gray-900 text-xs">{selectedMeeting.company_name || 'Unknown'}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 mb-0.5">Ticket Price</div>
                      <div className={`font-medium text-xs ${isFreeEvent ? 'text-blue-600' : 'text-green-600'}`}>
                        {isFreeEvent ? 'Free' : `₹${selectedMeeting.price || 0}`}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500 mb-0.5">Status</div>
                      <div className={`font-medium text-xs ${availableSpots > 5 ? 'text-green-600' : availableSpots > 0 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                        {availableSpots > 0 ? `${availableSpots} available` : 'Sold Out'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 text-center">
                <svg className="w-10 h-10 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>

                {meetings.length === 0 ? (
                  <>
                    <h3 className="text-base font-bold text-gray-900 mb-1">No Events Available</h3>
                    <p className="text-gray-600 text-xs">There are currently no public events to register.</p>
                  </>
                ) : (
                  <>
                    <h3 className="text-base font-bold text-gray-900 mb-1">Select an Event First</h3>
                    <p className="text-gray-600 text-xs">Please choose an event from the list to continue.</p>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      <style jsx>{`
        @keyframes star-movement {
          0% { transform: translate(0%, 0%); opacity: 0.6; }
          100% { transform: translate(-100%, 0%); opacity: 0; }
        }
        @keyframes fade-in {
          0% { opacity: 0; transform: translateY(20px) scale(0.95); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes slide-in {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-up {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-star-movement {
          animation: star-movement linear infinite alternate;
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .animate-slide-in {
          animation: slide-in 0.4s ease-out;
        }
        .animate-fade-up {
          animation: fade-up 0.4s ease-out;
        }
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default PublicEvents;